/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import SlTextarea from '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import { customElement, property, state } from 'lit/decorators.js';
import { autolink } from './utils.js';
// @ts-expect-error ts(1238)
let ChromedashTextarea = class ChromedashTextarea extends SlTextarea {
    constructor() {
        super(...arguments);
        this.cols = 50;
        this.rows = 10;
        this.offerMarkdown = false;
        this.isMarkdown = false;
        this.alwaysMarkdown = false;
        this.index = -1;
        this.showPreview = false;
    }
    /**
     * Checks whether the value conforms to custom validation constraints.
     * @return true if value is valid.
     */
    customCheckValidity(value) {
        if (this.multiple) {
            if (this.chromedash_split_pattern && this.chromedash_single_pattern) {
                const items = value.split(new RegExp(this.chromedash_split_pattern));
                const singleItemRegex = new RegExp('^' + this.chromedash_single_pattern + '$', '');
                const valid = items.every(item => {
                    if (!item) {
                        // ignore empty items
                        return true;
                    }
                    const itemValid = singleItemRegex.test(item);
                    return itemValid;
                });
                if (!valid) {
                    return false;
                }
            }
        }
        // If there is a pattern, and the value doesn't match the pattern, then fail.
        // This applies regardless whether this.multiple is true.
        if (this.pattern) {
            const valueRegex = new RegExp('^' + this.pattern + '$', '');
            return valueRegex.test(value);
        }
        // Otherwise, assume it is valid.  What about required? disabled?
        return true;
    }
    validate() {
        if (this.input === null) {
            return;
        }
        const invalidMsg = this.customCheckValidity(this.input.value)
            ? ''
            : 'invalid';
        if (this.setCustomValidity) {
            this.setCustomValidity(invalidMsg);
        }
    }
    firstUpdated() {
        this.validate();
    }
    updated() {
        if (!this.input) {
            return;
        }
        this.validate();
    }
    handleMarkdownChecked(e) {
        this.isMarkdown = Boolean(e.target?.checked);
        if (!this.isMarkdown) {
            this.showPreview = false;
        }
        // Note: The sl-change event continues to propagate to chromedash-form-field.
    }
    handlePreviewChecked(e) {
        this.showPreview = Boolean(e.target?.checked);
        e.stopPropagation();
    }
    render() {
        const editor = super.render();
        if (!this.offerMarkdown && !this.alwaysMarkdown) {
            return editor;
        }
        // Since this class subclasses SlTextarea, we need to render (but
        // not display) the content from that class, otherwise there will
        // be errors in validation code.
        const preview = html `
      <div style="display:none">${editor}</div>
      <div
        id="preview"
        style="border:var(--card-border); padding:0 var(--content-padding); min-height:14em; background:var(--table-alternate-background)"
      >
        ${autolink(this.value, [], true)}
      </div>
    `;
        return html `
      ${this.showPreview ? preview : editor}
      <sl-checkbox
        id="use-markdown"
        name="${this.name}_is_markdown"
        ?checked=${this.isMarkdown || this.alwaysMarkdown}
        ?disabled=${this.alwaysMarkdown}
        @sl-change=${this.handleMarkdownChecked}
      >
        Use markdown
      </sl-checkbox>
      <sl-icon-button
        name="info-circle"
        id="info-button"
        title="GitHub flavored markdown docs"
        href="https://docs.github.com/en/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax"
        target="_blank"
      ></sl-icon-button>
      ${this.isMarkdown || this.alwaysMarkdown
            ? html ` <sl-checkbox
              id="show-preview"
              ?checked=${this.showPreview}
              @sl-change=${this.handlePreviewChecked}
            >
              Preview
            </sl-checkbox>`
            : nothing}
    `;
    }
};
__decorate([
    property({ type: Boolean })
], ChromedashTextarea.prototype, "multiple", void 0);
__decorate([
    property({ type: String })
], ChromedashTextarea.prototype, "pattern", void 0);
__decorate([
    property({ type: String })
], ChromedashTextarea.prototype, "chromedash_single_pattern", void 0);
__decorate([
    property({ type: String })
], ChromedashTextarea.prototype, "chromedash_split_pattern", void 0);
__decorate([
    property({ type: Number })
], ChromedashTextarea.prototype, "cols", void 0);
__decorate([
    property({ type: Number })
], ChromedashTextarea.prototype, "rows", void 0);
__decorate([
    property({ type: Boolean })
], ChromedashTextarea.prototype, "offerMarkdown", void 0);
__decorate([
    property({ type: Boolean })
], ChromedashTextarea.prototype, "isMarkdown", void 0);
__decorate([
    property({ type: Boolean })
], ChromedashTextarea.prototype, "alwaysMarkdown", void 0);
__decorate([
    property({ type: Number }) // Represents which field this is on the form.
], ChromedashTextarea.prototype, "index", void 0);
__decorate([
    state()
], ChromedashTextarea.prototype, "showPreview", void 0);
ChromedashTextarea = __decorate([
    customElement('chromedash-textarea')
], ChromedashTextarea);
export { ChromedashTextarea };
